import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import CircleChart from '../CircleChart';
import { getDishAverages } from '../../utils/sustainabilityAverages';
import CO2eShowComponent from '../CO2eShowComponent';
import EthicsShow from '../EthicsShow';
import PriceMatricsComponent from '../PriceMatricsComponent';
import KsvCalculatorRow from '../KsvCalculatorRow';
import { updateDishImage, updatePortionSize, updateCookingWeightLoss, updateDishSalesPrice } from '../../api/api_dishes';
import { fetchDishByIdThunk, updateDishPortionSize as updateDishPortionSizeAction } from '../../store/slices/dishesReducer';
import { useTranslation } from '../../hooks/useTranslation';
import Select from 'react-select';
import { menuwiseApiBaseURL } from '../../api/api_base';


const DishSingleViewSummeryCard = ({
  item,
  onClick,
  className = '',
  darkMode = false,
  mealTypeTags = []
}) => {
  const dispatch = useDispatch();
  const averages = getDishAverages(item);
  const [editingImage, setEditingImage] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [imageUrlDraft, setImageUrlDraft] = useState("");
  const [imageFileDraft, setImageFileDraft] = useState(null);
  const [imageValidationError, setImageValidationError] = useState("");
  const [savingImage, setSavingImage] = useState(false);
  const [portionSize, setPortionSize] = useState("");
  const [savingPortionSize, setSavingPortionSize] = useState(false);
  const [portionNumber, setPortionNumber] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [showPortionSaveToast, setShowPortionSaveToast] = useState(false);
  const portionToastTimeoutRef = useRef(null);
  const [cookingWeightLoss, setCookingWeightLoss] = useState("");
  const [savingWeightLoss, setSavingWeightLoss] = useState(false);
  const [weightLossHasUnsavedChanges, setWeightLossHasUnsavedChanges] = useState(false);
  const [showWeightLossSaveButton, setShowWeightLossSaveButton] = useState(false);
  const [, setWeightLossUserHasInteracted] = useState(false);
  const [salesPrice, setSalesPrice] = useState("");
  const [savingSalesPrice, setSavingSalesPrice] = useState(false);
  const [salesPriceHasUnsavedChanges, setSalesPriceHasUnsavedChanges] = useState(false);
  const [showSalesPriceSaveButton, setShowSalesPriceSaveButton] = useState(false);
  // const [ethicsRecalcLoading, setEthicsRecalcLoading] = useState(false);
  const { t } = useTranslation();
  const maxImageSizeBytes = 2 * 1024 * 1024;
  const resolvedImageSrc = (() => {
    const image = item?.image;
    if (!image) return image;
    if (/^https?:\/\//i.test(image)) return image;

    if (image.startsWith('/')) {
      if (/^https?:\/\//i.test(menuwiseApiBaseURL)) {
        try {
          return `${new URL(menuwiseApiBaseURL).origin}${image}`;
        } catch (_e) {
          return image;
        }
      }
      return image;
    }

    return image;
  })();

  const triggerPortionSaveToast = () => {
    if (portionToastTimeoutRef.current) {
      clearTimeout(portionToastTimeoutRef.current);
    }
    setShowPortionSaveToast(true);
    portionToastTimeoutRef.current = setTimeout(() => {
      setShowPortionSaveToast(false);
      portionToastTimeoutRef.current = null;
    }, 2800);
  };

  useEffect(() => {
    return () => {
      if (portionToastTimeoutRef.current) {
        clearTimeout(portionToastTimeoutRef.current);
      }
    };
  }, []);

  //Ethic recalculation is dianamic, so we will not use it for now
  // const handleRecalcEthics = async () => {
  //   if (!item?.id || ethicsRecalcLoading) return;
  //   setEthicsRecalcLoading(true);
  //   try {
  //     await calculateDishEthics(item.id);
  //     dispatch(fetchDishByIdThunk(item.id));
  //   } catch (err) {
  //     console.error('Failed to recalculate dish ethics:', err);
  //   } finally {
  //     setEthicsRecalcLoading(false);
  //   }
  // };

  const openEditImage = () => {
    setEditingImage(true);
    setImageUrlDraft(item.image || "");
    setImageFileDraft(null);
    setImageValidationError("");
  };

  const cancelEditImage = () => {
    setEditingImage(false);
    setShowUploadModal(false);
    setImageUrlDraft("");
    setImageFileDraft(null);
    setImageValidationError("");
  };

  const saveImageUrl = async () => {
    if (!imageUrlDraft || savingImage) return;
    try {
      setSavingImage(true);
      await updateDishImage(item.id, imageUrlDraft);
      dispatch(fetchDishByIdThunk(item.id));
      cancelEditImage();
    } catch (error) {
      console.error('Failed to update dish image URL:', error);
    } finally {
      setSavingImage(false);
    }
  };

  const saveImage = async () => {
    if (!imageFileDraft || savingImage) return;

    try {
      setSavingImage(true);
      await updateDishImage(item.id, imageFileDraft);
      dispatch(fetchDishByIdThunk(item.id));
      cancelEditImage();
    } catch (error) {
      console.error('Failed to update dish image:', error);
    } finally {
      setSavingImage(false);
    }
  };

  // Calculate portion size automatically when portion number changes
  useEffect(() => {
    if (portionNumber > 0 && item.size) {
      const calculatedSize = Math.round(item.size / portionNumber);
      setPortionSize(calculatedSize.toString());
      // Check if this differs from the saved portion size
      const savedSize = item.portion_size || 0;
      const hasChanges = calculatedSize !== savedSize;
      setHasUnsavedChanges(hasChanges);

      // Show save button for 10 seconds only if user has interacted AND there are changes
      if (hasChanges && userHasInteracted) {
        setShowSaveButton(true);
        const timer = setTimeout(() => {
          setShowSaveButton(false);
        }, 10000); // 10 seconds

        return () => clearTimeout(timer);
      } else {
        setShowSaveButton(false);
      }
    } else {
      setPortionSize(item.portion_size ? item.portion_size.toString() : "");
      setHasUnsavedChanges(false);
      setShowSaveButton(false);
    }
  }, [portionNumber, item.size, item.portion_size, userHasInteracted]);

  const savePortionSize = async () => {
    if (savingPortionSize || !hasUnsavedChanges) return;
    setSavingPortionSize(true);
    try {
      const portionSizeValue = portionSize.trim() ? parseFloat(portionSize) : null;
      const response = await updatePortionSize(item.id, portionSizeValue);
      console.log(response);

      // Update Redux state directly - no need to refetch entire dish
      dispatch(updateDishPortionSizeAction(portionSizeValue));
      if (item?.id) {
        dispatch(fetchDishByIdThunk(item.id));
      }

      setHasUnsavedChanges(false);
      setShowSaveButton(false);
    } catch (error) {
      console.error('Failed to save portion size:', error);
    } finally {
      setSavingPortionSize(false);
    }
  };

  // Init portion count when switching dishes only.
  // Do not re-derive from size/portion_size on weight changes (adding components) —
  // Portioner is user-controlled; portionsstorlek follows size / portioner.
  const portionDishIdRef = useRef(null);
  useEffect(() => {
    const dishId = item?.id ?? null;
    if (portionDishIdRef.current === dishId && portionNumber > 0) {
      return;
    }
    portionDishIdRef.current = dishId;
    setUserHasInteracted(false);

    if (item?.size && item?.portion_size) {
      const totalSize = parseFloat(item.size);
      const savedPortionSize = parseFloat(item.portion_size);
      if (Number.isFinite(totalSize) && Number.isFinite(savedPortionSize) && savedPortionSize > 0) {
        setPortionNumber(Math.max(1, Math.round(totalSize / savedPortionSize)));
        return;
      }
    }
    setPortionNumber(1);
  }, [item?.id, item?.size, item?.portion_size, portionNumber]);

  // Initialize cooking weight loss from item data
  useEffect(() => {
    setCookingWeightLoss(item.cooking_weight_loss_percentage ? item.cooking_weight_loss_percentage.toString() : "");
  }, [item.cooking_weight_loss_percentage]);

  useEffect(() => {
    setSalesPrice(item.sales_price_total !== null && item.sales_price_total !== undefined ? String(item.sales_price_total) : "");
    setSalesPriceHasUnsavedChanges(false);
    setShowSalesPriceSaveButton(false);
  }, [item.sales_price_total]);

  const saveCookingWeightLoss = async () => {
    if (savingWeightLoss || !weightLossHasUnsavedChanges) return;
    setSavingWeightLoss(true);
    try {
      const weightLossValue = cookingWeightLoss.trim() ? parseFloat(cookingWeightLoss) : 0;
      const response = await updateCookingWeightLoss(item.id, weightLossValue);
      console.log(response);

      if (item?.id) {
        dispatch(fetchDishByIdThunk(item.id));
      }

      setWeightLossHasUnsavedChanges(false);
      setShowWeightLossSaveButton(false);
    } catch (error) {
      console.error('Failed to save cooking weight loss:', error);
    } finally {
      setSavingWeightLoss(false);
    }
  };

  const saveSalesPrice = async () => {
    if (savingSalesPrice || !salesPriceHasUnsavedChanges) return;
    setSavingSalesPrice(true);
    try {
      const value = salesPrice.trim() ? parseFloat(salesPrice) : null;
      await updateDishSalesPrice(item.id, value);
      if (item?.id) {
        dispatch(fetchDishByIdThunk(item.id));
      }
      setSalesPriceHasUnsavedChanges(false);
      setShowSalesPriceSaveButton(false);
    } catch (error) {
      console.error('Failed to save sales price:', error);
    } finally {
      setSavingSalesPrice(false);
    }
  };

  const handleKsvCalculated = (calculatedSalesPrice) => {
    const savedRaw = item.sales_price_total;
    const saved = savedRaw === null || savedRaw === undefined ? '' : String(savedRaw);
    const hasChanges = calculatedSalesPrice !== saved;

    setSalesPrice(calculatedSalesPrice);
    setSalesPriceHasUnsavedChanges(hasChanges);
    if (hasChanges) {
      setShowSalesPriceSaveButton(true);
      setTimeout(() => setShowSalesPriceSaveButton(false), 10000);
    }
  };

  const renderImage = () => (
    <div className="flex flex-col items-center relative">
      <div className="w-28 h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm relative group">
        {resolvedImageSrc && resolvedImageSrc !== "" ? (
          <img
            src={resolvedImageSrc}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              console.log("Image failed to load:", item.image);
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
            onLoad={(e) => {
              console.log("Image loaded successfully:", item.image);
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'none';
              }
            }}
          />
        ) : null}
        <div className="border-2 border-secondary-green rounded-xl flex items-center justify-center">
          <img
            src="/assets/icons/svg/default-image-placeholder.svg"
            alt="No bild"
            className={`w-12 h-12 text-gray-400 ${item.image && item.image !== "" ? 'hidden' : 'flex'} items-center justify-center`}
          />
        </div>

        {/* Modern Image Edit Overlay */}
        {editingImage && (
          <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col justify-center p-2">
            <input
              type="url"
              value={imageUrlDraft}
              onChange={(e) => setImageUrlDraft(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-secondary-green rounded-md focus:outline-none focus:ring-1 focus:ring-secondary-green focus:border-transparent bg-white"
              placeholder="https://..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveImageUrl();
                } else if (e.key === 'Escape') {
                  cancelEditImage();
                }
              }}
              onBlur={() => {
                if (imageUrlDraft !== (item.image || "")) {
                  saveImageUrl();
                } else {
                  cancelEditImage();
                }
              }}
              autoFocus
            />
            {savingImage && (
              <div className="mt-1 flex justify-center">
                <div className="w-4 h-4 border-2 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        )}

        {/* Modern Edit Button - Always visible */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditImage(); }}
          className="absolute bottom-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm transition-all duration-200 opacity-60 hover:opacity-100"
          title="Redigera bild"
        >
          <img
            src="/assets/icons/Redigera.png"
            alt="Edit"
            className="w-3 h-3 text-gray-600"
          />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUploadModal(true); }}
          className="absolute bottom-1 left-1 z-10 inline-flex items-center justify-center px-2 h-6 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm transition-all duration-200 opacity-70 hover:opacity-100 text-[10px]"
          title={t('Ladda upp bild')}
        >
          {t('Upload')}
        </button>
      </div>

      {/* Meal type tags under image */}
      <div className="flex justify-center gap-1 mt-2">
        {Object.entries(mealTypeTags).map(([tag, isActive]) => (
          <span
            key={`meal-${tag}`}
            className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium border cursor-help transition-colors ${isActive
              ? 'bg-green-100 text-green-800 border-green-200'
              : 'bg-gray-100 text-gray-500 border-gray-200'
              }`}
            title={tag === 'F' ? 'Frukost' : tag === 'L' ? 'Lunch' : 'Middag'}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );

  const renderUploadModal = () => {
    if (!showUploadModal) return null;
    return (
      <div className="fixed inset-0 z-[60] flex items-start justify-center pt-96 overflow-y-auto">
        <div className="absolute inset-0 bg-black/40" onClick={cancelEditImage} />
        <div className="relative bg-white rounded-xl p-4 w-full max-w-md mx-4">
          <h4 className="text-sm font-semibold mb-3">{t('Ladda upp bild')}</h4>
          <p className="text-xs text-gray-500 mb-2">{t('Endast JPEG/PNG, max 2 MB.')}</p>
          <input
            id="dish-image-upload-input"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setImageFileDraft(null);
                setImageValidationError("");
                return;
              }
              if (!['image/png', 'image/jpeg'].includes(file.type)) {
                setImageValidationError(t('Endast PNG och JPEG är tillåtet'));
                setImageFileDraft(null);
                return;
              }
              if (file.size > maxImageSizeBytes) {
                setImageValidationError(t('Max bildstorlek är 2 MB'));
                setImageFileDraft(null);
                return;
              }
              setImageValidationError("");
              setImageFileDraft(file);
            }}
            className="hidden"
          />
          <div className="flex items-center gap-3">
            <label
              htmlFor="dish-image-upload-input"
              className="inline-flex items-center justify-center px-3 py-1.5 text-sm border rounded cursor-pointer hover:bg-gray-50"
            >
              {t('Välj bild')}
            </label>
            <p className="text-xs text-gray-600">
              {imageFileDraft ? imageFileDraft.name : t('Ingen fil vald')}
            </p>
          </div>
          {!!imageValidationError && <p className="mt-2 text-xs text-red-600">{imageValidationError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={cancelEditImage}>
              {t('Avbryt')}
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm bg-secondary-green text-white rounded disabled:opacity-50"
              onClick={saveImage}
              disabled={!imageFileDraft || savingImage}
            >
              {savingImage ? t('Laddar upp...') : t('Ladda upp')}
            </button>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            {t('Tips: Du kan också lägga in en bildlänk via pennikonen.')}
          </p>
        </div>
      </div>
    );
  };

  const renderInfo = () => (
    <div>
      <span className="font-medium">{item.category?.name || 'Otilldelad'}</span>
      <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {/* Ethics information */}
        <div className="my-2 flex flex-wrap items-center gap-2">
          <EthicsShow
            isAnimal={item.is_animal}
            isAnimalKilled={item.is_animal_killed}
            isVegan={item.is_vegan}
            isVegetarian={item.is_vegetarian}
            ethicsUnknownConfirmed={item.ethics_unknown_confirmed}
            size="medium"
            showLabels={true}
            className={darkMode ? 'text-white' : ''}
          />
          {/* <button
            type="button"
            onClick={handleRecalcEthics}
            disabled={ethicsRecalcLoading}
            className={`text-xs underline ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              title={t("Beräkna etik utifrån ingredienser igen")}
          >
            {ethicsRecalcLoading ? '...' : 'Uppdatera etik'}
          </button> */}
        </div>
      </div>

      {/* Size and Unit */}
      <div className="flex items-center space-x-2">
        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('Storlek')}: {item.size} {item.unit_abbr}
        </span>
      </div>
      <div className="flex items-center space-x-1">
        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('Portioner')}:</span>
        <Select
          options={Array.from({ length: 100 }, (_, i) => ({ label: (i + 1).toString(), value: i + 1 }))}
          value={{ label: portionNumber.toString(), value: portionNumber }}
          onChange={(selected) => {
            const nextValue = selected?.value ?? 1;
            if (nextValue === portionNumber) {
              setUserHasInteracted(true);
              return;
            }

            setPortionNumber(nextValue);
            setUserHasInteracted(true);

            if (item.size) {
              const totalSize = parseFloat(item.size);
              if (Number.isFinite(totalSize) && nextValue > 0) {
                const recalculatedSize = Math.round(totalSize / nextValue);
                if (!Number.isNaN(recalculatedSize)) {
                  setPortionSize(recalculatedSize.toString());

                  const savedSize = item.portion_size || 0;
                  const hasChanges = recalculatedSize !== savedSize;
                  setHasUnsavedChanges(hasChanges);

                  if (hasChanges) {
                    setShowSaveButton(true);
                    triggerPortionSaveToast();
                    setTimeout(() => setShowSaveButton(false), 10000);
                  } else if (!hasChanges) {
                    setShowSaveButton(false);
                  }
                }
              }
            }
          }}
          placeholder={t('Antal portioner')}
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={false}
          isSearchable={false}
          styles={{
            control: (provided, state) => ({
              ...provided,
              minHeight: '28px',
              height: '28px',
              minWidth: '56px',
              fontSize: '12px',
              fontWeight: 'bold',
              color: darkMode ? 'white' : 'black',
              backgroundColor: darkMode ? '#374151' : 'white',
              borderColor: darkMode ? '#4B5563' : '#D1D5DB',
              '&:hover': {
                borderColor: darkMode ? '#6B7280' : '#9CA3AF',
              },
              boxShadow: 'none',
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: '0 8px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }),
            indicatorsContainer: (provided) => ({
              ...provided,
              height: '28px',
              width: '24px',
              padding: '0',
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            dropdownIndicator: (provided) => ({
              ...provided,
              padding: '4px 6px',
              width: '24px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }),
            singleValue: (provided) => ({
              ...provided,
              color: darkMode ? 'white' : 'black',
              fontSize: '12px',
              fontWeight: 'bold',
              margin: '0',
            }),
            menu: (provided) => ({
              ...provided,
              backgroundColor: darkMode ? '#374151' : 'white',
              border: `1px solid ${darkMode ? '#4B5563' : '#D1D5DB'}`,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected
                ? (darkMode ? '#4B5563' : '#3B82F6')
                : state.isFocused
                  ? (darkMode ? '#4B5563' : '#F3F4F6')
                  : 'transparent',
              color: state.isSelected
                ? 'white'
                : (darkMode ? 'white' : 'black'),
              fontSize: '12px',
              fontWeight: 'bold',
              padding: '6px 12px',
              '&:hover': {
                backgroundColor: state.isSelected
                  ? (darkMode ? '#4B5563' : '#3B82F6')
                  : (darkMode ? '#4B5563' : '#F3F4F6'),
              }
            }),
          }}
        />
      </div>
      {/* Portion Size Row - Auto-calculated with save option */}
      <div className="flex items-center space-x-1 mt-1">
        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('Portionsstorlek')}:
        </span>
        <div className="flex items-center space-x-1">
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
            {portionSize}g
          </span>
          {showSaveButton && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); savePortionSize(); }}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors duration-200"
              title="Spara portionsstorlek"
              disabled={savingPortionSize}
            >
              {savingPortionSize ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          )}
          {showPortionSaveToast && (
            <span className="ml-2 text-xs font-medium text-amber-600 whitespace-nowrap">
              Klick pa tick for att spara
            </span>
          )}
        </div>
      </div>

      {/* Cooking Weight Loss Row */}
      <div className="flex items-center space-x-1 mt-1">
        <div className="flex items-center space-x-1">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('Viktförlust')}:
          </span>
          <div className="relative group">
            <svg
              className={`w-3.5 h-3.5 cursor-help ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 w-64">
              <div className={`${darkMode ? 'bg-gray-800 text-gray-200 border-gray-700' : 'bg-white text-gray-700 border-gray-200'} rounded-lg shadow-lg border p-3 text-xs leading-relaxed`}>
                <p className="font-semibold mb-1">{t('Viktförlust vid tillagning')}</p>
                <p>{t('Ange hur många procent av vikten som försvinner när du tillagar rätten, till exempel vatten som avdunstar. Exempel: om en gryta kokar bort cirka 10 % vätska fyller du i 10. Tänk på att mycket höga värden (t.ex. 90 %) ger ett väldigt högt CO2e-värde per kg eftersom nästan ingen vikt återstår efter tillagning.')}</p>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent ${darkMode ? 'border-t-gray-800' : 'border-t-white'}`}></div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={cookingWeightLoss}
            onChange={(e) => {
              setCookingWeightLoss(e.target.value);
              setWeightLossUserHasInteracted(true);
              const savedValue = item.cooking_weight_loss_percentage || 0;
              const hasChanges = parseFloat(e.target.value) !== savedValue;
              setWeightLossHasUnsavedChanges(hasChanges);
              if (hasChanges) {
                setShowWeightLossSaveButton(true);
                setTimeout(() => setShowWeightLossSaveButton(false), 10000);
              }
            }}
            className={`w-16 px-1 py-0.5 text-xs border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-secondary-green`}
            placeholder="0"
          />
          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>%</span>
          {showWeightLossSaveButton && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveCookingWeightLoss(); }}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors duration-200"
              title={t("Spara viktförlust")}
              disabled={savingWeightLoss}
            >
              {savingWeightLoss ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Sales Price Row */}
      <div className="flex items-center space-x-1 mt-1">
        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('Försäljningspris')}:
        </span>
        <div className="flex items-center space-x-1">
          <input
            type="number"
            min="0"
            step="0.01"
            value={salesPrice}
            onChange={(e) => {
              const next = e.target.value;
              setSalesPrice(next);
              const savedRaw = item.sales_price_total;
              const saved = savedRaw === null || savedRaw === undefined ? '' : String(savedRaw);
              const hasChanges = next !== saved;
              setSalesPriceHasUnsavedChanges(hasChanges);
              if (hasChanges) {
                setShowSalesPriceSaveButton(true);
                setTimeout(() => setShowSalesPriceSaveButton(false), 10000);
              }
            }}
            onBlur={saveSalesPrice}
            className={`w-14 px-1 py-0.5 text-xs border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-secondary-green`}
            placeholder="0.00"
          />
          {showSalesPriceSaveButton && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveSalesPrice(); }}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors duration-200"
              title={t("Spara försäljningspris")}
              disabled={savingSalesPrice}
            >
              {savingSalesPrice ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="mt-1">
        <KsvCalculatorRow
          costPrice={item?.purchase_price_total}
          darkMode={darkMode}
          onCalculate={handleKsvCalculated}
        />
      </div>
    </div>
  );

  const renderCharts = () => (
    <div className="flex items-center space-x-4 justify-center">
      <CircleChart value={averages.environment_average || 0} size={64} strokeWidth={8} label={t("Miljö")} />
      <CircleChart value={averages.health_average || 0} size={64} strokeWidth={8} label={t("Hälsa")} />
      <CircleChart value={averages.social_average || 0} size={64} strokeWidth={8} label={t("Rättvisa")} />
      {/* <CircleChart value={100} size={64} strokeWidth={8} label="Transparens" /> */}
    </div>
  );


  // Default variant (same as hero but without hover effects)
  const sellPrice = item?.sales_price_total ?? null;
  const costPrice = item?.purchase_price_total ?? null;
  const marginValue = item?.price_difference ?? null;

  return (
    <>
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8 ${className}`}>
        <div className="flex flex-col tablet:flex-row laptop:flex-row desktop:flex-row tablet:items-center laptop:items-center desktop:items-center tablet:justify-between laptop:justify-between desktop:justify-between">
          <div className="flex items-start space-x-6">
            {renderImage()}
            {renderInfo()}
          </div>
          <div className="mt-6 tablet:mt-0 laptop:mt-0 desktop:mt-0 flex items-center space-x-6 justify-center tablet:justify-end w-full">
            {renderCharts()}
          </div>
          <div className="flex flex-col items-end space-y-2 w-[60%]">
            <CO2eShowComponent
              co2ePerKg={item.CO2e_menuwise}
              size={item.size}
              unit_id={item.unit_id}
              cookedCo2ePerKg={item.cooked_co2e_per_kg}
              cookingWeightLoss={item.cooking_weight_loss_percentage}
              portionSize={portionSize}
              fullWidth={true}
            />
            <div className="mt-4 flex justify-center w-full">
              <PriceMatricsComponent
                sellPrice={sellPrice}
                cost={costPrice}
                margin={marginValue}
              />
            </div>
          </div>
        </div>
      </div>
      {renderUploadModal()}
    </>
  );
};

export default DishSingleViewSummeryCard;
